import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Una tabla que en un teléfono se lee como fichas.
 *
 * En pantalla angosta la hoja de estilos apila las celdas y cada una muestra
 * el nombre de su columna adelante. Ese nombre tiene que salir de algún lado:
 * lo habitual es escribirlo a mano en cada celda con un `data-`, y son
 * cincuenta y cuatro celdas repartidas en ocho archivos. El problema no es el
 * trabajo, es que el día que alguien agregue o mueva una columna, la etiqueta
 * escrita a mano y el encabezado se separan **sin dar ningún error**: la ficha
 * pasa a decir «Canal» arriba de una versión.
 *
 * Acá la etiqueta no se escribe: se copia del `<th>` que le corresponde. No
 * pueden separarse porque son lo mismo, y una columna nueva funciona sola.
 *
 * El efecto corre después de cada render y no una sola vez, porque las filas
 * cambian con cada consulta. Recorre unas pocas decenas de nodos.
 */
export function Tabla({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLTableElement>(null);

  useEffect(() => {
    const tabla = ref.current;
    if (!tabla) return;

    const cabeceras = [...tabla.querySelectorAll('thead th')].map(
      (th) => th.textContent?.trim() ?? ''
    );
    if (cabeceras.length === 0) return;

    for (const fila of tabla.querySelectorAll('tbody tr')) {
      let columna = 0;
      for (const celda of fila.children) {
        const ancho = (celda as HTMLTableCellElement).colSpan || 1;
        // Una celda que abarca varias columnas no pertenece a ninguna: es una
        // fila de detalle, como la cascada de una traza. Etiquetarla con el
        // nombre de la primera diría algo falso.
        const etiqueta = ancho === 1 ? cabeceras[columna] : undefined;
        if (etiqueta) celda.setAttribute('data-etiqueta', etiqueta);
        else celda.removeAttribute('data-etiqueta');
        columna += ancho;
      }
    }
  });

  // La clase es la promesa de que las celdas van a tener etiqueta. La hoja la
  // usa para decidir si en un teléfono la tabla se apila en fichas: una tabla
  // sin ella se sigue arrastrando de costado, que es feo pero legible. Sin esa
  // distinción, una tabla que alguien escriba como `<table>` a secas quedaría
  // en el teléfono como una columna de valores sin decir de qué son.
  return (
    <table ref={ref} className={['con-fichas', className].filter(Boolean).join(' ')}>
      {children}
    </table>
  );
}
